import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import PostCard from '../components/PostCard';
import { Search, Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Home = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
      } catch (error) {
        setUser(null);
      }
    };

    fetchUser();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = {};
        if (searchQuery) params.search = searchQuery;
        if (selectedCategory) params.category = selectedCategory;

        const [postsRes, catsRes] = await Promise.all([
          axios.get(`${API}/posts`, { params }),
          axios.get(`${API}/categories`)
        ]);

        setPosts(postsRes.data);
        setCategories(catsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchQuery, selectedCategory]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = {};
    if (searchQuery) params.search = searchQuery;
    if (selectedCategory) params.category = selectedCategory;
    setSearchParams(params);
  };

  const handleCategoryFilter = (category) => {
    const newCategory = category === selectedCategory ? '' : category;
    setSelectedCategory(newCategory);
    const params = {};
    if (searchQuery) params.search = searchQuery;
    if (newCategory) params.category = newCategory;
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4 tracking-tight leading-tight">
            Artigos Recentes
          </h1>
        </div>

        <div className="mb-8 space-y-4">
          <form onSubmit={handleSearch} className="flex gap-2" data-testid="search-form">
            <div className="flex-1 relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-stone-400 w-5 h-5"
                strokeWidth={1.5}
              />
              <Input
                type="text"
                placeholder="Buscar artigos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-md"
                data-testid="search-input"
              />
            </div>
            <Button type="submit" className="rounded-md" data-testid="search-submit">
              Buscar
            </Button>
          </form>

          {categories.length > 0 && (
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 text-stone-600">
                <Filter className="w-4 h-4" strokeWidth={1.5} />
                <span className="text-sm font-medium">Categorias:</span>
              </div>
              {categories.slice(0, 10).map((cat) => (
                <button
                  key={cat.name}
                  onClick={() => handleCategoryFilter(cat.name)}
                  className={`text-sm px-3 py-1.5 rounded-md transition-all duration-200 ${
                    selectedCategory === cat.name
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                  }`}
                  data-testid={`category-filter-${cat.name}`}
                >
                  {cat.name} ({cat.count})
                </button>
              ))}
              {(searchQuery || selectedCategory) && (
                <Button
                  onClick={clearFilters}
                  variant="ghost"
                  size="sm"
                  className="rounded-md"
                  data-testid="clear-filters-button"
                >
                  <X className="w-4 h-4 mr-1" strokeWidth={1.5} />
                  Limpar
                </Button>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-lg text-stone-600">Nenhum artigo encontrado</p>
            {user && (
              <Link to="/dashboard" className="text-primary hover:underline mt-4 inline-block">
                Adicionar feeds RSS no dashboard
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="posts-grid">
            {posts.map((post) => (
              <PostCard key={post.post_id} post={post} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Home;